import React, { useState, useEffect } from "react";
import { 
  FiSend, 
  FiChevronDown, 
  FiMail, 
  FiPhone, 
  FiMessageCircle, 
  FiLifeBuoy, 
  FiCheckCircle, 
  FiClock,
  FiCornerDownRight,
  FiHelpCircle,
  FiTrash2
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../../api/axiosInstance";

const Help = () => {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState("");
  const [queries, setQueries] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [replyingQueryId, setReplyingQueryId] = useState(null);
  const [userReplyText, setUserReplyText] = useState("");

  useEffect(() => {
    fetchQueries();

    const handleHelpdeskUpdate = () => {
      fetchQueries();
    };

    window.addEventListener("helpdesk_updated", handleHelpdeskUpdate);
    const interval = setInterval(() => {
      fetchQueries();
    }, 10000);

    return () => {
      window.removeEventListener("helpdesk_updated", handleHelpdeskUpdate);
      clearInterval(interval);
    };
  }, []);

  const fetchQueries = async () => {
    try {
      const res = await axiosInstance.get("/users/helpdesk");
      setQueries(res.data.queries || []);
    } catch (err) {
      console.error("Error fetching queries:", err);
    }
  };

  const deleteQuery = async (id) => {
    if (!window.confirm("Are you sure you want to delete this query ticket?")) return;
    try {
      await axiosInstance.delete(`/users/helpdesk/${id}`);
      setQueries((prev) => prev.filter((q) => q._id !== id));
    } catch (err) {
      console.error("Error deleting query:", err);
      alert("Failed to delete query.");
    }
  };

  const handleUserReply = async (queryId) => {
    if (!userReplyText.trim()) return;
    try {
      const res = await axiosInstance.post(`/users/helpdesk/${queryId}/reply`, { message: userReplyText });
      if (res.data.success) {
        setQueries((prev) =>
          prev.map((q) => (q._id === queryId ? res.data.query : q))
        );
        setUserReplyText("");
        setReplyingQueryId(null);
        alert("Reply sent to admin successfully!");
      }
    } catch (err) {
      console.error("User reply error:", err);
      alert(err.response?.data?.message || "Failed to send reply");
    }
  };

  const submitQuery = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSending(true);
    try {
      const res = await axiosInstance.post("/users/helpdesk", { message });
      setQueries([res.data.query, ...queries]);
      setMessage("");
      setSuccess("Your message has been sent successfully.");
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      alert("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const faqs = [
    { q: "Can I cancel a booking?", a: "Yes! You can cancel up to 24 hours before the appointment through your 'My Bookings' dashboard." },
    { q: "How do I contact a vendor?", a: "Once a booking is confirmed, you can view the vendor's contact details or use the 'Instant Chat' feature on their profile." },
    { q: "I forgot my password", a: "Use the 'Forgot Password' link on the login page to receive a secure reset link via your registered email." },
    { q: "When will I get a reply from support?", a: "Our support team typically reviews and responds to all queries within 24 business hours." },
  ];

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6 lg:p-8 space-y-6 md:space-y-8 font-sans">
      
      {/* 1. Header Section */}
      <div className="flex flex-col items-center text-center space-y-2 pb-2">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#134DA7] shadow-sm mb-1">
          <FiLifeBuoy size={24} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-800 tracking-tight">Help & Support</h1>
          <p className="text-gray-500 text-[10px] sm:text-xs max-w-xs mx-auto">
            Experience premium assistance tailored to your needs
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 items-start">
        
        {/* 2. Left Column: Main Content Area */}
        <div className="lg:col-span-2 space-y-6 md:space-y-8">
          
          {/* Section: Consultation Form */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-gray-50/50 px-4 py-3 sm:px-5 sm:py-3.5 border-b border-gray-100 flex items-center gap-3">
              <div className="p-1.5 bg-blue-100 rounded-lg text-[#134DA7]">
                <FiMessageCircle size={16} />
              </div>
              <h2 className="text-xs sm:text-sm font-bold text-gray-800 uppercase tracking-wider">New Inquiry</h2>
            </div>
            
            <div className="p-5 md:p-6">
              <form onSubmit={submitQuery} className="space-y-4">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your issue or ask a question..."
                  rows={4}
                  className="w-full p-4 bg-gray-50/30 border border-gray-100 rounded-xl focus:bg-white focus:border-blue-400 focus:ring-4 focus:ring-blue-50 outline-none text-gray-700 transition-all resize-none text-sm placeholder:text-gray-300 font-medium"
                  required
                />

                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full sm:w-auto px-8 py-2.5 bg-[#134DA7] hover:bg-[#0f4392] text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-70 text-sm"
                  >
                    {sending ? "Processing..." : "Submit Inquiry"}
                    {!sending && <FiSend size={14} />}
                  </button>
                  {success && (
                    <motion.span 
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-2 text-green-600 text-xs font-bold bg-green-50 px-3 py-1.5 rounded-lg border border-green-100"
                    >
                      <FiCheckCircle size={14} /> {success}
                    </motion.span>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* Section: Support History */}
          {queries.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="bg-gray-50/50 px-4 py-3 sm:px-5 sm:py-3.5 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-indigo-100 rounded-lg text-indigo-600">
                    <FiClock size={16} />
                  </div>
                  <h2 className="text-xs sm:text-sm font-bold text-gray-800 uppercase tracking-wider">Log</h2>
                </div>
                <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded uppercase tracking-widest">
                  {queries.length} Entries
                </span>
              </div>

              <div className="divide-y divide-gray-50">
                {queries.map((q) => {
                  const isResolved = q.status?.toLowerCase() === "resolved" || q.status?.toLowerCase() === "closed" || Boolean(q.resolvedAt) || Boolean(q.isResolved);
                  return (
                    <div key={q._id} className="p-4 sm:p-5 hover:bg-gray-50/30 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div className="text-[9px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-widest flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-300"></span>
                          {new Date(q.createdAt).toLocaleString("en-IN", {
                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${
                            isResolved ? "bg-emerald-100 text-emerald-700 border border-emerald-100" : "bg-amber-100 text-amber-700 border border-amber-100"
                          }`}>
                            {isResolved ? "Resolved" : (q.status || "Pending")}
                          </span>
                          <button
                            onClick={() => deleteQuery(q._id)}
                            className="text-gray-400 hover:text-red-600 transition p-1"
                            title="Delete query"
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 font-semibold text-xs sm:text-[13px] leading-relaxed mb-3">{q.message}</p>
                      
                      {(() => {
                        const responseList = q.responses && q.responses.length > 0
                          ? q.responses
                          : (q.response ? [{ message: q.response, sender: "admin", createdAt: q.respondedAt }] : []);

                        if (responseList.length === 0) return null;

                        return (
                          <div className="mt-3 space-y-2">
                            {responseList.map((item, idx) => {
                              const isAdmin = item.sender === "admin";
                              return (
                                <div
                                  key={idx}
                                  className={`p-3.5 rounded-xl border relative ${
                                    isAdmin
                                      ? "bg-blue-50/50 border-blue-100/70"
                                      : "bg-gray-50 border-gray-200 ml-3"
                                  }`}
                                >
                                  <div
                                    className={`flex items-center gap-2 font-black text-[8px] sm:text-[9px] mb-1 uppercase tracking-widest ${
                                      isAdmin ? "text-[#134DA7]" : "text-gray-600"
                                    }`}
                                  >
                                    <FiCornerDownRight size={12} /> {isAdmin ? "Admin Response" : "You (Reply)"}
                                  </div>
                                  <p className="text-gray-700 text-xs font-medium leading-relaxed">{item.message}</p>
                                  <p className="text-[9px] text-gray-400 mt-1 font-semibold">
                                    {new Date(item.createdAt || q.respondedAt || q.updatedAt).toLocaleString("en-IN", {
                                      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
                                    })}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* User Reply Option (only if ticket is not resolved) */}
                      {!isResolved ? (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          {replyingQueryId === q._id ? (
                            <div className="space-y-2">
                              <textarea
                                rows={2}
                                value={userReplyText}
                                onChange={(e) => setUserReplyText(e.target.value)}
                                placeholder="Type your reply to admin..."
                                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs outline-none focus:bg-white focus:border-blue-400 font-medium"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleUserReply(q._id)}
                                  className="px-4 py-1.5 bg-[#134DA7] text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition"
                                >
                                  Send Reply
                                </button>
                                <button
                                  onClick={() => { setReplyingQueryId(null); setUserReplyText(""); }}
                                  className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-200"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setReplyingQueryId(q._id)}
                              className="text-[11px] font-bold text-[#134DA7] hover:underline flex items-center gap-1"
                            >
                              <FiMessageCircle size={13} /> Reply to Admin
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="mt-3 text-[10px] font-bold text-emerald-600 flex items-center gap-1">
                          <FiCheckCircle size={12} /> Resolved by Admin
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section: FAQ Repository */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-gray-50/50 px-4 py-3 sm:px-5 sm:py-3.5 border-b border-gray-100 flex items-center gap-3">
              <div className="p-1.5 bg-amber-100 rounded-lg text-amber-600">
                <FiHelpCircle size={16} />
              </div>
              <h2 className="text-xs sm:text-sm font-bold text-gray-800 uppercase tracking-wider">FAQ</h2>
            </div>

            <div className="p-3 sm:p-4 md:p-5 grid gap-2">
              {faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={false}
                  className={`group border rounded-xl transition-all duration-300 ${
                    openFaq === i 
                      ? "border-blue-100 bg-blue-50/10 shadow-sm" 
                      : "border-gray-50 hover:border-gray-100"
                  }`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full text-left p-3 sm:p-4 flex justify-between items-center gap-3 sm:gap-4 group"
                  >
                    <span className={`text-[12px] sm:text-[13px] font-bold transition-colors duration-300 ${
                      openFaq === i ? "text-[#134DA7]" : "text-gray-600 group-hover:text-gray-800"
                    }`}>
                      {faq.q}
                    </span>
                    <div className={`p-1 rounded sm:p-1.5 sm:rounded-lg transition-all duration-300 ${
                      openFaq === i ? "bg-[#134DA7] text-white rotate-180" : "bg-gray-50 text-gray-400 group-hover:bg-gray-100"
                    }`}>
                      <FiChevronDown size={14} />
                    </div>
                  </button>
                  
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-0">
                          <p className="text-gray-500 text-[11px] sm:text-[12px] leading-relaxed font-medium pl-2 border-l-2 border-blue-100">
                            {faq.a}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* 3. Right Column: Contact Sidebar */}
        <div className="space-y-4 lg:sticky lg:top-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-[#134DA7] p-4 sm:p-5 text-white">
              <h2 className="text-sm sm:text-base font-bold leading-tight">Elite Support</h2>
              <p className="text-blue-100 text-[9px] sm:text-[10px] mt-1 tracking-wide opacity-80 uppercase font-bold">
                Available 24/7
              </p>
            </div>
            
            <div className="p-3 sm:p-4 space-y-2">
              <a href="mailto:support@easymarg.com" className="flex items-center gap-2 p-2.5 sm:p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all group border border-gray-100">
                <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg bg-blue-50 text-[#134DA7] flex items-center justify-center shrink-0">
                  <FiMail size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[7px] sm:text-[8px] uppercase font-bold tracking-widest text-gray-400">Email</p>
                  <p className="text-[10px] sm:text-[11px] font-bold text-gray-700 whitespace-nowrap">support@easymarg.com</p>
                </div>
              </a>

              <a href="tel:+919930919980" className="flex items-center gap-2 p-2.5 sm:p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all group border border-gray-100">
                <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                  <FiPhone size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[7px] sm:text-[8px] uppercase font-bold tracking-widest text-gray-400">Call</p>
                  <p className="text-[10px] sm:text-[11px] font-bold text-gray-700 whitespace-nowrap">+91 99309 19980</p>
                </div>
              </a>
            </div>
          </div>

          {/* New: Status Widget */}
         
        </div>
      </div>
    </div>

  );
};

export default Help;